import { useCallback, useMemo, useState } from 'react';
import type { TemplateDto } from '@chat-emp/shared';

import {
  createTemplate as apiCreate,
  deleteTemplate as apiDelete,
  listTemplates as apiList,
  updateTemplate as apiUpdate,
} from '../lib/facade.js';
import { emitToHost } from '../lib/bridge.js';

export interface UseTemplates {
  templates: TemplateDto[];
  loading: boolean;
  error: string | null;
  fetch: () => Promise<void>;
  /** Resolves to the failure message, or null once saved. */
  create: (name: string, template: string) => Promise<string | null>;
  update: (id: string, name: string, template: string) => Promise<string | null>;
  remove: (id: string) => Promise<boolean>;
  canCreateMore: boolean;
}

const message = (e: unknown): string =>
  e instanceof Error && e.message ? e.message : 'Something went wrong.';

/** Templates CRUD for the composer panel. Delete is optimistic with rollback;
 *  create is gated at 10 user templates (matching stream-chat). */
export function useTemplates(): UseTemplates {
  const [templates, setTemplates] = useState<TemplateDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setTemplates(await apiList());
    } catch (e) {
      setError(message(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const create = useCallback(async (name: string, template: string) => {
    try {
      const created = await apiCreate({ name, template });
      setTemplates((prev) => [...prev, created]);
      emitToHost('template-create', { name, template });
      return null;
    } catch (e) {
      return message(e);
    }
  }, []);

  const update = useCallback(async (id: string, name: string, template: string) => {
    try {
      const updated = await apiUpdate(id, { name, template });
      setTemplates((prev) => prev.map((t) => (t.id === id ? updated : t)));
      emitToHost('template-save', { id, name, template });
      return null;
    } catch (e) {
      return message(e);
    }
  }, []);

  const remove = useCallback(
    async (id: string) => {
      setError(null);
      const prev = templates;
      setTemplates((cur) => cur.filter((t) => t.id !== id)); // optimistic
      try {
        await apiDelete(id);
        return true;
      } catch (e) {
        setTemplates(prev); // rollback
        setError(message(e));
        return false;
      }
    },
    [templates],
  );

  const canCreateMore = useMemo(
    () => templates.filter((t) => !t.isSystem).length < 10,
    [templates],
  );

  return { templates, loading, error, fetch, create, update, remove, canCreateMore };
}
